import { createSlice } from '@reduxjs/toolkit'
import { requests } from '../../../core/ajax'
import { feedback } from '../../../core/feedback'
import { mapBackendInstallerToFrontend } from '../constants/prescriptor-installers-mappers.constants'

const prescriptorInstallersSlice = createSlice({
  name: 'prescriptorInstallers',
  initialState: {
    isLoading: false,
    data: [],
    search: {},
    pagination: {},
    selectedInstaller: null,
  },
  reducers: {
    setLoading: (state, { payload }) => {
      state.isLoading = payload
    },
    setInstallersData: (state, { payload }) => {
      state.data = payload.data
      if (payload.search) state.search = payload.search
      if (payload.pagination) state.pagination = payload.pagination
      state.isLoading = false
    },
    setSelectedInstaller: (state, { payload }) => {
      state.selectedInstaller = payload
    },
    clearSelectedInstaller: (state) => {
      state.selectedInstaller = null
    },
  },
})

export const prescriptorInstallersReducer = prescriptorInstallersSlice.reducer

export const { setLoading, setInstallersData, setSelectedInstaller, clearSelectedInstaller } =
  prescriptorInstallersSlice.actions

export const fetchInstallers = (searchParams, prescriptorId) => (dispatch) => {
  const { _pagina = 1, _num_x_pagina = 10, ...restParams } = searchParams
  const { get } = requests

  dispatch(setLoading(true))
  get(`/prescriptor/${prescriptorId}/instaladores`, {
    params: { ...restParams, _pagina, _num_x_pagina },
  })
    .then(({ data }) => {
      const mappedInstallers = (data.instaladores || data).map(mapBackendInstallerToFrontend)
      dispatch(
        setInstallersData({
          data: mappedInstallers,
          search: searchParams,
          pagination: {
            total_paginas: data.pagination?.total_paginas || data['total_paginas'] || 1,
            total_instaladores:
              data.pagination?.total_instaladores ||
              data['total_instaladores'] ||
              mappedInstallers.length,
          },
        })
      )
    })
    .catch(({ response }) => {
      console.error('Error recuperando instaladores del prescriptor', response)
      feedback('error', 'No se han podido cargar los instaladores')
      dispatch(setLoading(false))
    })
}

export const createInstaller =
  (payload, prescriptorId, onSuccess, onError) => (dispatch, getState) => {
    const { post } = requests
    const search = getState().prescriptorInstallers.search

    post(`/prescriptor/${prescriptorId}/instaladores`, payload)
      .then(() => {
        feedback('success', 'Instalador creado correctamente')
        dispatch(fetchInstallers(search, prescriptorId))
        if (onSuccess) onSuccess()
      })
      .catch(({ response }) => {
        const errorMessage =
          response?.data?.message || response?.data?.detail || 'No se ha podido crear el instalador'
        feedback('error', errorMessage)
        if (onError) onError()
      })
  }

export const updateInstaller =
  (installerId, payload, prescriptorId, onSuccess, onError) => (dispatch, getState) => {
    const { putById } = requests
    const search = getState().prescriptorInstallers.search

    putById(`/prescriptor/${prescriptorId}/instaladores`, installerId, payload)
      .then(() => {
        feedback('success', 'Instalador actualizado correctamente')
        dispatch(fetchInstallers(search, prescriptorId))
        if (onSuccess) onSuccess()
      })
      .catch(({ response }) => {
        const errorMessage =
          response?.data?.message ||
          response?.data?.detail ||
          'No se ha podido actualizar el instalador'
        feedback('error', errorMessage)
        if (onError) onError()
      })
  }

export const toggleInstallerActive =
  (installerId, currentActive, prescriptorId) => (dispatch, getState) => {
    const { patchById } = requests
    const search = getState().prescriptorInstallers.search

    patchById(`/prescriptor/${prescriptorId}/instaladores`, `${installerId}/activo`, {
      activo: !currentActive,
    })
      .then(() => {
        feedback('success', currentActive ? 'Instalador desactivado' : 'Instalador activado')
        dispatch(fetchInstallers(search, prescriptorId))
      })
      .catch(({ response }) => {
        const errorMessage =
          response?.data?.message ||
          response?.data?.detail ||
          'No se ha podido cambiar el estado del instalador'
        feedback('error', errorMessage)
      })
  }

export const deleteInstaller = (installerId, prescriptorId, onSuccess) => (dispatch, getState) => {
  const { deleteById } = requests
  const search = getState().prescriptorInstallers.search

  deleteById(`/prescriptor/${prescriptorId}/instaladores`, installerId)
    .then(() => {
      feedback('success', 'Instalador eliminado correctamente')
      dispatch(fetchInstallers(search, prescriptorId))
      if (onSuccess) onSuccess()
    })
    .catch(({ response }) => {
      const errorMessage =
        response?.data?.message ||
        response?.data?.detail ||
        'No se ha podido eliminar el instalador'
      feedback('error', errorMessage)
    })
}

export const instaladorColaboradorAutocomplete = (nif, prescriptorId) => () => {
  const { get } = requests

  return get(`/prescriptor/${prescriptorId}/instaladores/buscar`, {
    params: { nif },
  })
    .then(({ data }) => {
      if (!data) return null
      return mapBackendInstallerToFrontend(data) 
    })
    .catch(({ response }) => {
      console.error('Error al consultar autocompletado de instalador:', response)
      return null
    })
}